import React from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13, tint12 } from "../../../../theme/b13Tokens";
import PhaseCard, { PhaseCardValue } from "./PhaseCard";
import WorkflowFlowStrip from "./WorkflowFlowStrip";
import { MIN_ACCURACY_NON_STARTING, AMBER_WARN_BELOW } from "./workflowConstants";
import WaitingPhaseCard from "./WaitingPhaseCard";
import AddPhaseModal from "./AddPhaseModal";
// T63 Part O — TransitionsSummary import removed; the always-visible
// bottom summary duplicated the phase-card destination chips.
// import TransitionsSummary from "./TransitionsSummary";

// Batch 7 — shared body used by both the Settings route and the
// program-editor's WorkflowQuickModal. Consumers own state and SAVE.

// Drag-reorder client key. Assigned once per phase, never persisted.
// Both consumers whitelist the payload they send (Editor/index.tsx,
// WorkflowQuickModal.tsx), so this UI-only field never reaches the API.
let _dndSeq = 0;
const nextDndId = () => `P${++_dndSeq}`;

export type EditorPhase = PhaseCardValue;

export function makeEmptyPhase(order: number): EditorPhase {
  return {
    phaseName: "",
    colour: LegacyTokens.blue,
    icon: null,
    description: "",
    entryRule: "",
    advancementRule: "",
    regressionRule: "",
    transitionsTo: "",
    masteredTargetStatus: "",
    notMasteredTransitionsTo: "",
    minPercentCorrect: 80,
    acrossSessions: 3,
    minTrialsPerSession: 5,
    promptRequirement: "any",
    manualApprovalRequired: false,
    isStarting: false,
    isTerminal: false,
    isActive: true,
    countsTowardAutoOpenLimit: false,
    onlyProbeFirst: false,
    autoAddPhaseLineOnEnter: false,
    thresholdValue: null,
    metricType: null,
    comparisonOperator: null,
    orderNumber: order,
    _dndId: nextDndId(),
  };
}

export function normalisePhase(raw: any, i: number): EditorPhase {
  return {
    id: raw?.id,
    _dndId: raw?._dndId ?? nextDndId(),
    phaseName: raw?.phaseName ?? "",
    colour: raw?.colour || LegacyTokens.blue,
    icon: raw?.icon ?? null,
    description: raw?.description ?? "",
    entryRule: raw?.entryRule ?? "",
    advancementRule: raw?.advancementRule ?? "",
    regressionRule: raw?.regressionRule ?? "",
    transitionsTo: raw?.transitionsTo ?? "",
    masteredTargetStatus: raw?.masteredTargetStatus ?? "",
    notMasteredTransitionsTo: raw?.notMasteredTransitionsTo ?? "",
    minPercentCorrect: Number(raw?.minPercentCorrect ?? 80),
    acrossSessions: Number(raw?.acrossSessions ?? 3),
    minTrialsPerSession: Number(raw?.minTrialsPerSession ?? 5),
    promptRequirement: raw?.promptRequirement ?? "any",
    manualApprovalRequired: !!raw?.manualApprovalRequired,
    isStarting: !!raw?.isStarting,
    isTerminal: !!raw?.isTerminal,
    isActive: raw?.isActive !== false,
    countsTowardAutoOpenLimit: !!raw?.countsTowardAutoOpenLimit,
    onlyProbeFirst: !!raw?.onlyProbeFirst,
    autoAddPhaseLineOnEnter: !!raw?.autoAddPhaseLineOnEnter,
    thresholdValue: raw?.thresholdValue !== undefined && raw?.thresholdValue !== null
      ? Number(raw.thresholdValue) : null,
    metricType: raw?.metricType ?? null,
    comparisonOperator: raw?.comparisonOperator ?? null,
    orderNumber: raw?.orderNumber ?? i,
  };
}

/**
 * Batch 7 §7.5 — per-phase accuracy validation. Returns error/warn text
 * (or null) for the Min Percent Correct field. Starting phases allow 0.
 * Non-starting phases below MIN_ACCURACY_NON_STARTING → hard error.
 * Non-starting phases in [MIN, AMBER_WARN_BELOW) → amber warn.
 */
export function accuracyValidation(v: EditorPhase): { error: string | null; warn: string | null } {
  const val = v.minPercentCorrect;
  if (v.isStarting) return { error: null, warn: null };
  if (!Number.isFinite(val) || val < MIN_ACCURACY_NON_STARTING) {
    return {
      error: `Minimum ${MIN_ACCURACY_NON_STARTING}% for a non-starting phase (baseline phases can be 0).`,
      warn: null,
    };
  }
  if (val < AMBER_WARN_BELOW) {
    return {
      error: null,
      warn: `Below ${AMBER_WARN_BELOW}% is unusual for a non-starting phase — confirm this is intended.`,
    };
  }
  return { error: null, warn: null };
}

export type WorkflowEditorBodyProps = {
  workflowName: string;
  onWorkflowNameChange: (v: string) => void;
  workflowNameError: string | null;

  objective: string;                  // rendered in the OBJECTIVE: subheader
  // T63 Part O — canonical objective slug ("increase_frequency" etc.)
  // so PhaseCard can render the correct criterion shape (frequency
  // count vs. percent correct) without string-matching labels.
  objectiveKey?: string | null;

  phases: EditorPhase[];
  onPhasesChange: (updater: (prev: EditorPhase[]) => EditorPhase[]) => void;

  targetsCountsByPhaseName?: Record<string, number>;   // for remove-blocked reason

  // Batch 17 / 7C §7C.5 — auto-open workflow-scoped settings, rendered
  // by the WaitingPhaseCard below the phase list.
  autoOpenEnabled: boolean;
  autoOpenMaxSimultaneous: number | null;
  autoOpenInitialPhaseIndex: number | null;   // 0-based index into phases[]
  autoOpenDirection: string | null;
  onAutoOpenChange: (patch: {
    autoOpenEnabled?: boolean;
    autoOpenMaxSimultaneous?: number | null;
    autoOpenInitialPhaseIndex?: number | null;
    autoOpenDirection?: string | null;
  }) => void;

  registerNameRef?: (el: HTMLInputElement | null) => void;
  readOnly?: boolean;
};

export default function WorkflowEditorBody(props: WorkflowEditorBodyProps) {
  const {
    workflowName, onWorkflowNameChange, workflowNameError,
    objective, phases, onPhasesChange,
    targetsCountsByPhaseName = {},
    autoOpenEnabled, autoOpenMaxSimultaneous, autoOpenInitialPhaseIndex, autoOpenDirection,
    onAutoOpenChange,
    registerNameRef, readOnly,
  } = props;

  // Batch 7C §7C.2 — which insert-position is currently opening the
  // Add-new-phase modal. null → modal closed.
  const [insertAtIdx, setInsertAtIdx] = React.useState<number | null>(null);

  // Aug 2026 hardening: patch enforces workflow-graph invariants
  // client-side so the UI cannot present a contradictory state
  // between clicks. Backend validateGraph() is still authoritative;
  // this just mirrors those rules for a friendlier UX:
  //   - Turning `isStarting` ON for phase i turns it OFF for every
  //     other phase (exactly-one starting).
  //   - Turning `isTerminal` ON for phase i turns it OFF for every
  //     other phase AND clears i's `transitionsTo` (a terminal phase
  //     cannot point anywhere — this is what the backend rejects).
  const patch = (i: number, p: Partial<EditorPhase>) =>
    onPhasesChange((P) => {
      const startingOn = p.isStarting === true;
      const terminalOn = p.isTerminal === true;
      // Multi-phase workflows CANNOT have the same phase flagged
      // both Starting AND Final (backend rule R4). Single-phase
      // workflows are exempt — one phase can legitimately be both.
      const isMultiPhase = P.length > 1;
      return P.map((x, k) => {
        if (k === i) {
          const next = { ...x, ...p };
          if (terminalOn) next.transitionsTo = "";
          // SAME-phase mutual exclusion for multi-phase workflows —
          // flipping one flag ON forces the other OFF on the same
          // phase, so the toggle can never present a contradictory
          // state between clicks.
          if (isMultiPhase && startingOn) next.isTerminal = false;
          if (isMultiPhase && terminalOn) next.isStarting = false;
          return next;
        }
        // Sibling: auto-mutually-exclude when this patch flips ON.
        if (startingOn && x.isStarting) return { ...x, isStarting: false };
        if (terminalOn && x.isTerminal) return { ...x, isTerminal: false };
        return x;
      });
    });

  // Batch 18 — the three array-mutating helpers below must ALSO remap
  // autoOpenInitialPhaseIndex when the array shifts, otherwise a phase
  // that Kerthiga picked as the initial gets silently rebound to
  // whatever ends up at that index after a drag / duplicate / remove.
  // Same defect class as the intervals-on-save issue: an index into a
  // list is a stable pointer only until someone rewrites the list.
  const remapAutoOpenIdxForMove = (from: number, to: number): number | null => {
    if (autoOpenInitialPhaseIndex === null) return null;
    let idx = autoOpenInitialPhaseIndex;
    if (idx === from) return to;
    if (from < to && idx > from && idx <= to) return idx - 1;
    if (from > to && idx >= to && idx < from) return idx + 1;
    return idx;
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= phases.length) return;
    const nextIdx = remapAutoOpenIdxForMove(from, to);
    onPhasesChange((P) => {
      const c = [...P]; const [x] = c.splice(from, 1); c.splice(to, 0, x);
      return c.map((p, i) => ({ ...p, orderNumber: i }));
    });
    if (nextIdx !== autoOpenInitialPhaseIndex) {
      onAutoOpenChange({ autoOpenInitialPhaseIndex: nextIdx });
    }
  };

  // Drag-reorder sensors. PointerSensor.activationConstraint.distance
  // is 4 px so a click on the handle doesn't count as an immediate drag.
  // KeyboardSensor + sortableKeyboardCoordinates gives Space=pick up,
  // Arrow=move, Space=drop, Escape=cancel.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // CRITICAL — the drag path MUST route through move(from, to) above,
  // NOT through arrayMove or a private splice. move() calls
  // remapAutoOpenIdxForMove() BEFORE the array is spliced (Batch 18);
  // a parallel drag path that skips that call would silently rebind
  // autoOpenInitialPhaseIndex to whichever phase ends up at the
  // pointer's stale index after the reorder. Same defect class as the
  // intervals-on-save issue this codebase already fought.
  // Track drag state so AddPhaseBars can hide while a card is in
  // flight. Bars staying visible between moving cards reads as "drag
  // is broken" even when the drop is correct — they don't participate
  // in the sortable transform because they aren't sortable items.
  const [isDragging, setIsDragging] = React.useState(false);
  const onDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = phases.findIndex((p) => (p._dndId ?? "") === active.id);
    const to = phases.findIndex((p) => (p._dndId ?? "") === over.id);
    if (from < 0 || to < 0) return;
    move(from, to);
  };

  const duplicate = (i: number) => {
    // Duplicate inserts at i+1; every index > i shifts by 1. The initial-
    // phase pointer stays on the original, so bump only if it was strictly
    // above the insertion point.
    if (autoOpenInitialPhaseIndex !== null && autoOpenInitialPhaseIndex > i) {
      onAutoOpenChange({ autoOpenInitialPhaseIndex: autoOpenInitialPhaseIndex + 1 });
    }
    onPhasesChange((P) => {
      const c = [...P];
      c.splice(i + 1, 0, {
        ...c[i], id: undefined,
        // Fresh _dndId so the copy is a distinct sortable — otherwise
        // duplicate would ship two rows with the same key and dnd-kit
        // would refuse to reorder either.
        _dndId: nextDndId(),
        phaseName: c[i].phaseName + " (copy)",
        isStarting: false, isTerminal: false,
      });
      return c.map((p, k) => ({ ...p, orderNumber: k }));
    });
  };

  const remove = (i: number) => {
    // Removing the initial phase itself nulls the pointer (fail-visible
    // via WaitingPhaseCard's "unavailable" note). Removing a phase above
    // the pointer shifts it down by one; below is unaffected.
    if (autoOpenInitialPhaseIndex !== null) {
      if (autoOpenInitialPhaseIndex === i) {
        onAutoOpenChange({ autoOpenInitialPhaseIndex: null });
      } else if (autoOpenInitialPhaseIndex > i) {
        onAutoOpenChange({ autoOpenInitialPhaseIndex: autoOpenInitialPhaseIndex - 1 });
      }
    }
    onPhasesChange((P) =>
      P.length <= 1 ? P : P.filter((_, k) => k !== i).map((p, k) => ({ ...p, orderNumber: k }))
    );
  };

  // Batch 7C Step 4 — insert a phase at position `at` (0..phases.length).
  // Every existing transitionsTo is preserved verbatim (they name-match
  // sibling phases; inserting a new phase doesn't rename any existing
  // one). autoOpenInitialPhaseIndex is remapped in the same shape as
  // duplicate(): a phase inserted at or below the pointer shifts it up
  // by one. Same helper set as Batch 18 rather than a fourth remapping
  // in a different style — per your instruction.
  const insertAt = (at: number, defaults: { phaseName: string; colour: string; icon: string | null }) => {
    if (autoOpenInitialPhaseIndex !== null && autoOpenInitialPhaseIndex >= at) {
      onAutoOpenChange({ autoOpenInitialPhaseIndex: autoOpenInitialPhaseIndex + 1 });
    }
    onPhasesChange((P) => {
      const c = [...P];
      const seed = makeEmptyPhase(at);
      seed.phaseName = defaults.phaseName;
      seed.colour = defaults.colour;
      seed.icon = defaults.icon;
      c.splice(at, 0, seed);
      return c.map((p, k) => ({ ...p, orderNumber: k }));
    });
  };


  // Handle phase-rename: propagate to any `transitionsTo` currently
  // pointing at the OLD name so a rename doesn't break the graph.
  const editPhase = (i: number, next: { phaseName: string; colour: string; icon: string | null }) => {
    const oldName = phases[i].phaseName;
    onPhasesChange((P) =>
      P.map((p, k) => {
        if (k === i) return { ...p, phaseName: next.phaseName, colour: next.colour, icon: next.icon };
        // Path B: rename must propagate through BOTH transition edges
        // so a phase-name change never dangles either branch.
        const patched: EditorPhase = { ...p };
        if (patched.transitionsTo === oldName) patched.transitionsTo = next.phaseName;
        if (patched.notMasteredTransitionsTo === oldName) patched.notMasteredTransitionsTo = next.phaseName;
        return patched;
      })
    );
  };

  const activeNames = phases
    .filter((p) => p.isActive && p.phaseName.trim())
    .map((p) => p.phaseName.trim());

  // Batch 7 §2 fix — icon+colour lookup by phase name for the
  // resulting-status chip in PhaseCard. Built from ALL named phases
  // (active or inactive), so a chip pointing at an inactive-but-defined
  // phase still resolves.
  const phasesByName: Record<string, { icon: string | null; colour: string }> = {};
  for (const p of phases) {
    const n = p.phaseName.trim();
    if (n) phasesByName[n] = { icon: p.icon, colour: p.colour };
  }

  return (
    <Box>
      {/* Batch 7C §7C.6 — workflow name field moved to the BOTTOM of
         the editor (immediately above the modal's CANCEL/SAVE). The
         header just carries the OBJECTIVE subheader now. Validation,
         focus-on-invalid, and the view-mode title are all state-driven
         (they read `workflowName` from parent state, not the input's
         position in the DOM), so moving the field is position-safe.
         Natural tab order preserved — no positive tabIndex used. */}

      {/* OBJECTIVE: subheader — derived from the workflow's objective */}
      <Typography
        sx={{
          ...TypeScale.fieldLabel,
          color: LegacyTokens.ink3, mb: 1.5,
        }}
      >
        OBJECTIVE: {(objective || "").toUpperCase() || "—"}
      </Typography>

      {/* Read-only flow strip (§7.2 — user's decision: keep, condensed) */}
      <WorkflowFlowStrip
        phases={phases.filter((p) => p.isActive).map((p) => ({
          phaseName: p.phaseName, colour: p.colour, icon: p.icon,
        }))}
      />

      <Typography sx={{ ...TypeScale.sectionHead, mb: 1.5 }}>Phases</Typography>

      {/* Phase cards + inline ADD NEW PHASE bars (§7C.2) between every
         adjacent pair and after the last card, then the Waiting phase
         card at the bottom (§7C.5).

         DnD wrapping: SortableContext.items lists ONLY phase _dndIds.
         AddPhaseBars render inside DndContext but are not registered
         sortable items, so dnd-kit's closestCenter never picks them as
         drop targets — a drop always lands on a phase card. The bars
         stay visually put during a drag (they don't participate in the
         sortable transform), which reads as slightly noisy but keeps
         the drop routing correct. onDragEnd delegates to move(from, to)
         so the Batch-18 auto-open-pointer remap runs on the drag path
         too — NOT arrayMove, which would bypass it. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setIsDragging(true)}
        onDragCancel={() => setIsDragging(false)}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={phases.map((p) => p._dndId ?? "")}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={1.5}>
            {phases.map((p, i) => {
              const acc = accuracyValidation(p);
              const targetsInPhase = targetsCountsByPhaseName[p.phaseName] ?? 0;
              const canRemove = targetsInPhase === 0;
              const removeReason = targetsInPhase > 0
                ? `Cannot remove — ${targetsInPhase} target${targetsInPhase === 1 ? "" : "s"} currently in this phase.`
                : null;
              // Sibling names for the Next Phase select (active, named, not self).
              const siblings = activeNames.filter((n) => n !== p.phaseName.trim());
              return (
                <React.Fragment key={p._dndId ?? `${p.id ?? "new"}-${i}`}>
                  <PhaseCard
                    value={p}
                    sortableId={p._dndId}
                    index={i}
                    total={phases.length}
                    siblingPhaseNames={siblings}
                    phasesByName={phasesByName}
                    onChange={(patchObj) => patch(i, patchObj)}
                    onEditPhase={(next) => editPhase(i, next)}
                    onRemove={() => remove(i)}
                    onDuplicate={() => duplicate(i)}
                    onMoveUp={() => move(i, i - 1)}
                    onMoveDown={() => move(i, i + 1)}
                    canRemove={canRemove}
                    removeBlockedReason={removeReason}
                    minAccuracyError={acc.error}
                    minAccuracyWarn={acc.warn}
                    readOnly={readOnly}
                    objectiveKey={props.objectiveKey ?? null}
                  />
                  {!readOnly && !isDragging && (
                    <AddPhaseBar onClick={() => setInsertAtIdx(i + 1)} />
                  )}
                </React.Fragment>
              );
            })}
          </Stack>
        </SortableContext>
      </DndContext>
      <Stack spacing={1.5}>
        <WaitingPhaseCard
          phases={phases}
          autoOpenEnabled={autoOpenEnabled}
          autoOpenMaxSimultaneous={autoOpenMaxSimultaneous}
          autoOpenInitialPhaseIndex={autoOpenInitialPhaseIndex}
          autoOpenDirection={autoOpenDirection}
          onChange={onAutoOpenChange}
          readOnly={readOnly}
        />
      </Stack>

      {/* T63 Part O — the always-visible Transitions summary duplicates
         the destination chips already on each phase card and does not
         appear in the HiRasmus reference. Removed from the standard
         builder; the graph reads cleanly top-to-bottom via phase
         headers alone. TransitionsSummary component preserved for any
         future collapsible "Workflow Summary" affordance. */}

      {/* Batch 7C §7C.6 — workflow name at the bottom, immediately
         above the modal's CANCEL/SAVE row. */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: 3 }}>
        <TextField
          fullWidth size="medium" label="Workflow name"
          value={workflowName}
          disabled={readOnly}
          onChange={(e) => onWorkflowNameChange(e.target.value)}
          inputRef={registerNameRef}
          error={Boolean(workflowNameError)}
          helperText={workflowNameError || " "}
          InputLabelProps={{ shrink: true }}
          FormHelperTextProps={{ sx: { color: workflowNameError ? LegacyTokens.red : LegacyTokens.ink3 } }}
          sx={workflowNameError
            ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: LegacyTokens.red, borderWidth: 2 } }
            : undefined}
        />
      </Stack>

      <AddPhaseModal
        open={insertAtIdx !== null}
        onCancel={() => setInsertAtIdx(null)}
        onSave={(next) => {
          if (insertAtIdx !== null) {
            insertAt(insertAtIdx, next);
            setInsertAtIdx(null);
          }
        }}
      />
    </Box>
  );
}

// Batch 7C §7C.2 — full-width dashed insert affordance that spans the
// gap between two phase cards (and appears after the last one). Same
// visual language as the pre-Step-4 trailing "+ Add phase" button, now
// inserted at every join.
const AddPhaseBar: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button
    fullWidth
    onClick={onClick}
    startIcon={<AddIcon />}
    sx={{
      py: 1,
      border: `1.5px dashed ${LegacyTokens.line}`,
      color: B13.primary,
      textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
      fontSize: 12,
      backgroundColor: "transparent",
      "&:hover": { borderColor: B13.primary, backgroundColor: tint12(B13.primary) },
    }}
  >
    Add new phase
  </Button>
);
